/**
 * Campaigns: a named message to a frozen list of people.
 *
 * CREATING ONE FROM A SAVED VIEW is the path this screen is built around,
 * because it is the one the brief's walkthrough describes: filter, save, send.
 * The recipients are resolved by the BFF through the CRM at CREATION and
 * stored — so what the counters count is a list somebody could have reviewed,
 * not "whoever matches when the send fires".
 *
 * PREVIEW BEFORE SEND, always. The preview renders through the same function
 * the send uses, so a contact with no first name shows its blank here rather
 * than in eight hundred WhatsApp messages.
 */
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@meshhub/design-tokens/ui';
import { Ban, Eye, Play, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FormDialog,
  MutationError,
  SelectField,
  TextField,
} from '../../components/FormKit.web.jsx';
import { PAGE_SIZE } from '../../constants/density.js';
import { filterFromStoredJson, toFilterInput } from '../../lib/filters.js';
import { formatCount, formatDateTime } from '../../lib/format.js';
import {
  CAMPAIGNS_QUERY,
  CAMPAIGN_PREVIEW_QUERY,
  CANCEL_CAMPAIGN,
  CREATE_CAMPAIGN,
  SAVED_VIEWS_QUERY,
  SCHEDULE_CAMPAIGN,
  TEMPLATES_QUERY,
  WHATSAPP_ACCOUNTS_QUERY,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const STATE_LABELS = {
  DRAFT: S.app.inactive,
  SCHEDULED: S.messaging.schedule,
  RUNNING: S.app.loading,
  DONE: S.messaging.statusSent,
  CANCELLED: S.app.cancel,
};

export function CampaignsScreen() {
  const [creating, setCreating] = useState(false);
  const [previewOf, setPreviewOf] = useState(null);

  const { data, loading, refetch } = useQuery(CAMPAIGNS_QUERY, {
    variables: { page: 1, pageSize: PAGE_SIZE },
  });
  const { data: viewData } = useQuery(SAVED_VIEWS_QUERY, { errorPolicy: 'ignore' });
  const { data: templateData } = useQuery(TEMPLATES_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  const [schedule, scheduleState] = useMutation(SCHEDULE_CAMPAIGN, {
    onCompleted: () => refetch(),
  });
  const [cancel, cancelState] = useMutation(CANCEL_CAMPAIGN, { onCompleted: () => refetch() });

  const campaigns = data?.campaigns?.items ?? [];

  return (
    <div className="flex h-screen flex-col">
      <header
        className="flex h-toolbar shrink-0 items-center gap-4 border-b border-border
                   bg-surface px-6"
      >
        <h1 className="text-lg font-bold">{S.messaging.campaigns}</h1>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={14} aria-hidden="true" />
          {S.messaging.newCampaign}
        </Button>
        <Link to="/messaging/outbox" className="ms-auto text-sm text-primary">
          {S.messaging.outbox}
        </Link>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <MutationError error={scheduleState.error ?? cancelState.error} />
        {loading && campaigns.length === 0 && (
          <div className="flex justify-center p-7">
            <Spinner />
          </div>
        )}
        {!loading && campaigns.length === 0 && <EmptyState title={S.app.notFound} />}

        {campaigns.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{S.messaging.campaignName}</TH>
                <TH>{S.messaging.recipients}</TH>
                <TH>{S.messaging.status}</TH>
                <TH>{S.messaging.counters}</TH>
                <TH>{S.messaging.schedule}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {campaigns.map((campaign) => (
                <TR key={campaign.id}>
                  <TD className="font-medium">{campaign.name}</TD>
                  <TD className="tabular-nums">{formatCount(campaign.resolvedCount)}</TD>
                  <TD>
                    <Badge className="bg-surface-hover">
                      {STATE_LABELS[campaign.state] ?? campaign.state}
                    </Badge>
                  </TD>
                  <TD className="text-sm text-text-muted tabular-nums">
                    {S.messaging.statusSent} {formatCount(campaign.sentCount)} ·{' '}
                    {S.messaging.statusRead} {formatCount(campaign.readCount)} ·{' '}
                    {S.messaging.statusSuppressed} {formatCount(campaign.suppressedCount)}
                  </TD>
                  <TD className="text-text-muted">
                    {campaign.scheduledAt ? formatDateTime(campaign.scheduledAt) : S.app.none}
                  </TD>
                  <TD>
                    <span className="flex gap-1">
                      <Button variant="ghost" onClick={() => setPreviewOf(campaign)}>
                        <Eye size={13} aria-hidden="true" />
                        {S.messaging.preview}
                      </Button>
                      {['DRAFT', 'SCHEDULED'].includes(campaign.state) && (
                        <Button
                          variant="ghost"
                          onClick={() => schedule({ variables: { id: campaign.id } })}
                        >
                          <Play size={13} aria-hidden="true" />
                          {S.messaging.sendNow}
                        </Button>
                      )}
                      {campaign.state !== 'CANCELLED' && (
                        <Button
                          variant="ghost"
                          onClick={() => cancel({ variables: { id: campaign.id } })}
                        >
                          <Ban size={13} aria-hidden="true" />
                          {S.messaging.cancel}
                        </Button>
                      )}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateCampaignDialog
        open={creating}
        onOpenChange={setCreating}
        views={viewData?.savedViews ?? []}
        templates={templateData?.messageTemplates ?? []}
        onCreated={() => refetch()}
      />
      <PreviewDialog campaign={previewOf} onClose={() => setPreviewOf(null)} />
    </div>
  );
}

function CreateCampaignDialog({ open, onOpenChange, views, templates, onCreated }) {
  const [name, setName] = useState('');
  const [viewId, setViewId] = useState('');
  const [templateId, setTemplateId] = useState('');
  // WHICH NUMBER THIS CAMPAIGN SENDS FROM. Empty means the default, resolved
  // and FROZEN by the server at creation — a campaign built this morning must
  // not change identity because somebody re-pointed the default before it ran
  // tonight. The picker only appears once there is a choice to make.
  const [accountId, setAccountId] = useState('');
  const { data: accountsData } = useQuery(WHATSAPP_ACCOUNTS_QUERY, { skip: !open });
  const accounts = (accountsData?.whatsappAccounts ?? []).filter((a) => a.isActive);
  const [create, { loading, error }] = useMutation(CREATE_CAMPAIGN);

  const submit = async () => {
    const view = views.find((v) => v.id === viewId);
    await create({
      variables: {
        name: name.trim(),
        templateId: templateId || null,
        // The filter is sent, and the BFF resolves it against the CRM once.
        // The client does not send ids it never held.
        filter: view ? toFilterInput(filterFromStoredJson(view.filterJson)) : null,
        accountId: accountId || null,
      },
    });
    setName('');
    setViewId('');
    setTemplateId('');
    setAccountId('');
    onOpenChange(false);
    onCreated();
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.messaging.newCampaign}
      submitLabel={S.app.create}
      onSubmit={submit}
      busy={loading}
      canSubmit={Boolean(name.trim() && viewId && templateId)}
      error={error}
    >
      <TextField
        label={S.messaging.campaignName}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <SelectField
        label={S.messaging.fromView}
        value={viewId}
        onChange={(event) => setViewId(event.target.value)}
      >
        <option value="">{S.app.none}</option>
        {views.map((view) => (
          <option key={view.id} value={view.id}>
            {view.name}
          </option>
        ))}
      </SelectField>
      <SelectField
        label={S.messaging.template}
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value)}
      >
        <option value="">{S.app.none}</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </SelectField>
      {/* Only with something to choose between. On a deployment with one
          number a picker holding one option is a decision nobody has. */}
      {accounts.length > 1 && (
        <SelectField
          label={S.whatsapp.sendsFrom}
          hint={S.whatsapp.defaultHint}
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
        >
          <option value="">
            {accounts.find((a) => a.isDefault)?.label ?? S.whatsapp.isDefault}
          </option>
          {accounts.map((account) => (
            <option key={account.id} value={account.accountId}>
              {account.label}
            </option>
          ))}
        </SelectField>
      )}
    </FormDialog>
  );
}

function PreviewDialog({ campaign, onClose }) {
  const [load, { data, loading }] = useLazyQuery(CAMPAIGN_PREVIEW_QUERY);
  React.useEffect(() => {
    if (campaign) load({ variables: { id: campaign.id, sample: 5 } });
  }, [campaign, load]);

  return (
    <FormDialog
      open={Boolean(campaign)}
      onOpenChange={(next) => !next && onClose()}
      title={`${S.messaging.preview} — ${campaign?.name ?? ''}`}
      submitLabel={S.app.close}
      onSubmit={onClose}
      canSubmit
    >
      {loading && <Spinner />}
      <div className="flex flex-col gap-2">
        {(data?.campaignPreview ?? []).map((entry, index) => (
          <Card key={index} className="p-3">
            <p className="text-sm font-medium">{entry.displayName}</p>
            <p className="whitespace-pre-wrap text-sm text-text-muted">{entry.renderedBody}</p>
          </Card>
        ))}
      </div>
    </FormDialog>
  );
}
